import React from "react";
import { toast } from "sonner";
import { X, ScanLine, Loader2, Keyboard, CameraOff, Phone, MapPin, Package, ArrowRight, CheckCircle2 } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { driverApi } from "../lib/api";

const SCANNER_ID = "ema-driver-qr-scanner";

export default function DriverQRScannerModal({ open, onClose, onScanned }) {
  const [phase, setPhase] = React.useState("idle"); // idle | starting | scanning | submitting | success | error
  const [manualToken, setManualToken] = React.useState("");
  const [showManual, setShowManual] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState("");
  const [result, setResult] = React.useState(null);
  const scannerRef = React.useRef(null);
  const stoppedRef = React.useRef(false);

  const cleanup = React.useCallback(async () => {
    try {
      const s = scannerRef.current;
      if (s && s.isScanning) {
        await s.stop();
      }
      if (s) {
        try { await s.clear(); } catch (_) { /* noop */ }
      }
    } catch (_) { /* noop */ }
    scannerRef.current = null;
    stoppedRef.current = true;
  }, []);

  const submitToken = React.useCallback(async (token) => {
    if (!token) return;
    setPhase("submitting");
    try {
      const { data } = await driverApi().post("/driver/scan", { token: token.trim() });
      setResult(data.booking);
      setPhase("success");
      await cleanup();
      if (data.first_scan) toast.success("Pakke afhentet — undervejs til modtager");
      else toast.success("Booking åbnet");
      onScanned?.(data.booking);
    } catch (e) {
      const msg = e?.response?.data?.detail || "QR-kode kunne ikke valideres";
      setErrMsg(msg);
      setPhase("error");
      toast.error(msg);
    }
  }, [cleanup, onScanned]);

  // Start camera scanner when modal opens
  React.useEffect(() => {
    if (!open || showManual) return;
    let cancelled = false;
    stoppedRef.current = false;
    setPhase("starting");
    setErrMsg("");

    const start = async () => {
      try {
        const s = new Html5Qrcode(SCANNER_ID, { verbose: false });
        scannerRef.current = s;
        await s.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1 },
          (decodedText) => {
            if (cancelled || stoppedRef.current) return;
            stoppedRef.current = true;
            submitToken(decodedText);
          },
          () => { /* per-frame failures — ignore */ }
        );
        if (!cancelled) setPhase("scanning");
      } catch (e) {
        if (cancelled) return;
        console.error("Scanner start failed:", e);
        setErrMsg(e?.message || "Kamera kunne ikke startes — tjek tilladelser.");
        setPhase("error");
      }
    };
    start();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [open, showManual, submitToken, cleanup]);

  // ESC + body scroll lock
  React.useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const handleClose = async () => {
    await cleanup();
    setShowManual(false);
    setManualToken("");
    setResult(null);
    setErrMsg("");
    setPhase("idle");
    onClose?.();
  };

  const tryManual = (e) => {
    e.preventDefault();
    if (!manualToken.trim()) { toast.error("Indtast QR-token"); return; }
    submitToken(manualToken.trim());
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center" data-testid="driver-qr-modal">
      <div className="absolute inset-0 bg-black/85" onClick={handleClose} />
      <div className="relative bg-white w-full sm:max-w-md sm:mx-4 max-h-[95vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-brand-dark text-white px-5 py-4 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-brand-red flex items-center justify-center"><ScanLine size={18} /></div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-white/60">Driver app</div>
              <div className="font-display font-semibold text-base">Scan QR-kode</div>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-white/10" data-testid="qr-modal-close" aria-label="Luk">
            <X size={20} />
          </button>
        </div>

        {/* Scanner / states */}
        <div className="p-4 sm:p-5">
          {phase === "success" && result ? (
            <div data-testid="qr-success">
              <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-300">
                <CheckCircle2 size={20} className="text-emerald-700 flex-shrink-0" />
                <div>
                  <div className="font-display font-semibold text-emerald-900">Booking åbnet</div>
                  <div className="font-mono text-xs text-emerald-800">{result.booking_number}</div>
                </div>
              </div>
              <h3 className="font-display font-semibold text-lg text-brand-dark mt-5">{result.customer_name}</h3>

              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex gap-3 items-start">
                  <MapPin size={16} className="text-brand-red mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-[10px] tracking-widest uppercase text-brand-text2">Afhentning</div>
                    <div className="text-brand-dark font-medium">{result.pickup_address}, {result.pickup_zip} {result.pickup_city}</div>
                    <a href={`tel:${result.customer_phone}`} className="text-brand-red text-xs inline-flex items-center gap-1 mt-1"><Phone size={11} /> {result.customer_phone}</a>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <ArrowRight size={16} className="text-brand-text2 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-[10px] tracking-widest uppercase text-brand-text2">Levering</div>
                    <div className="text-brand-dark font-medium">{result.delivery_address}, {result.delivery_zip} {result.delivery_city}</div>
                    {result.recipient_phone && <a href={`tel:${result.recipient_phone}`} className="text-brand-red text-xs inline-flex items-center gap-1 mt-1"><Phone size={11} /> {result.recipient_phone}</a>}
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <Package size={16} className="text-brand-text2 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 grid grid-cols-3 gap-2 text-xs">
                    <div><div className="text-brand-text2 uppercase tracking-widest text-[9px]">Pakker</div><div className="font-semibold text-brand-dark">{result.package_count}</div></div>
                    <div><div className="text-brand-text2 uppercase tracking-widest text-[9px]">Vægt</div><div className="font-semibold text-brand-dark">{result.weight_kg} kg</div></div>
                    <div><div className="text-brand-text2 uppercase tracking-widest text-[9px]">Beløb</div><div className="font-semibold text-brand-dark">{result.total_amount_dkk} kr</div></div>
                  </div>
                </div>
                {result.notes && (
                  <div className="bg-amber-50 border-l-2 border-amber-500 px-3 py-2 text-xs">
                    <div className="font-semibold text-amber-900 uppercase tracking-widest text-[10px]">Note fra kunde</div>
                    <div className="text-brand-dark mt-1">{result.notes}</div>
                  </div>
                )}
              </dl>

              <div className="grid grid-cols-2 gap-2 mt-5">
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${result.delivery_address}, ${result.delivery_zip} ${result.delivery_city}`)}`}
                  target="_blank" rel="noreferrer"
                  className="bg-brand-dark text-white text-center py-3 font-semibold text-sm uppercase tracking-wider"
                  data-testid="qr-result-navigate"
                >
                  Naviger til modtager
                </a>
                <button onClick={handleClose} className="border-2 border-brand-dark text-brand-dark py-3 font-semibold text-sm uppercase tracking-wider" data-testid="qr-result-close">
                  Luk
                </button>
              </div>
            </div>
          ) : showManual ? (
            <form onSubmit={tryManual} className="space-y-4" data-testid="qr-manual-form">
              <div>
                <label className="label-flat">QR-token (manuelt)</label>
                <input
                  className="input-flat font-mono text-sm"
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value)}
                  placeholder="Indtast token fra kundens skærm"
                  autoFocus
                  data-testid="qr-manual-input"
                />
                <div className="text-[11px] text-brand-text2 mt-2">Brug kun hvis kamera ikke virker — token ses ved at trykke på kundens QR-kode.</div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowManual(false)} className="flex-1 border-2 border-brand-dark text-brand-dark py-3 font-semibold text-sm uppercase tracking-wider">
                  Tilbage til scanner
                </button>
                <button type="submit" disabled={phase === "submitting"} className="flex-1 bg-brand-red text-white py-3 font-semibold text-sm uppercase tracking-wider disabled:opacity-50" data-testid="qr-manual-submit">
                  {phase === "submitting" ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Åbn booking"}
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="relative w-full aspect-square bg-black overflow-hidden" id={SCANNER_ID} data-testid="qr-camera-container">
                {phase === "starting" && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-3">
                    <Loader2 className="animate-spin" size={28} />
                    <div className="text-xs uppercase tracking-widest">Starter kamera...</div>
                  </div>
                )}
                {phase === "error" && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-3 px-6 text-center">
                    <CameraOff size={28} className="text-brand-red" />
                    <div className="text-sm font-semibold">{errMsg || "Kamera kunne ikke startes"}</div>
                    <div className="text-xs text-white/60">Tjek at du har givet tilladelse til kamera.</div>
                  </div>
                )}
                {phase === "submitting" && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-3 bg-black/60">
                    <Loader2 className="animate-spin" size={28} />
                    <div className="text-xs uppercase tracking-widest">Åbner booking...</div>
                  </div>
                )}
                {/* Scanner overlay frame */}
                {phase === "scanning" && (
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="w-[250px] h-[250px] border-4 border-brand-red/80 relative">
                      <span className="absolute -top-1 -left-1 w-5 h-5 border-l-4 border-t-4 border-brand-red" />
                      <span className="absolute -top-1 -right-1 w-5 h-5 border-r-4 border-t-4 border-brand-red" />
                      <span className="absolute -bottom-1 -left-1 w-5 h-5 border-l-4 border-b-4 border-brand-red" />
                      <span className="absolute -bottom-1 -right-1 w-5 h-5 border-r-4 border-b-4 border-brand-red" />
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 text-center">
                <p className="text-brand-dark font-semibold text-sm">Bed kunden om at vise QR-koden på sin telefon</p>
                <p className="text-brand-text2 text-xs mt-1">Hold kameraet ca. 10–20 cm fra skærmen. Koden læses automatisk.</p>
              </div>

              <button onClick={() => setShowManual(true)} className="mt-5 w-full border-2 border-brand text-brand-text2 py-3 text-xs font-semibold uppercase tracking-wider hover:border-brand-dark hover:text-brand-dark flex items-center justify-center gap-2" data-testid="qr-toggle-manual">
                <Keyboard size={14} /> Indtast token manuelt
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
